import * as THREE from 'three';
import {
    normalizeRevolveAxisDirection,
} from '../../../feature/interaction/revolve/RevolveAngleDragMath';
import type { RevolveAngleDragContour } from '../../../feature/interaction/revolve/RevolveAngleDragTypes';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/mesh/feature/revolve 层；
 * 2. 负责封装旋转拖拽过程中的轮廓 ghost 预览；
 * 3. 该对象只展示拖拽中的临时轮廓位置，不参与正式 CAD 结果。
 *
 * 文件职责：
 * 1. 从轮廓构建闭合 Line 预览；
 * 2. 按当前角度更新 ghost 位置；
 * 3. 显式释放 Line 资源。
 */

/** Ghost 轮廓颜色。 */
const GHOST_LINE_COLOR = 0x4cb6ff;
/** Ghost 轮廓渲染顺序。 */
const GHOST_RENDER_ORDER = 79;

/**
 * 旋转拖拽 ghost 对象。
 */
export class RevolveDragGhostObject extends THREE.Group {
    /** 原始轮廓点列。 */
    private baseLoops: THREE.Vector3[][] = [];
    /** 轮廓线集合。 */
    private ghostLines: THREE.Line[] = [];
    /** 轮廓线点属性集合。 */
    private ghostPositionAttributes: THREE.BufferAttribute[] = [];
    /** 旋转四元数复用对象。 */
    private readonly rotationQuaternion = new THREE.Quaternion();
    /** 旋转点复用对象。 */
    private readonly rotatedPoint = new THREE.Vector3();

    /**
     * 构造 ghost 对象。
     */
    public constructor() {
        super();
        this.name = '__revolve_drag_face_position__';
        this.visible = false;
    }

    /**
     * 设置 ghost 轮廓。
     *
     * @param contours 轮廓集合。
     */
    public setContours(contours: RevolveAngleDragContour[]): void {
        this.baseLoops = [];
        contours.forEach((contour) => {
            if (Array.isArray(contour.outer) && contour.outer.length >= 3) {
                this.baseLoops.push(contour.outer.map((point) => new THREE.Vector3(point[0], point[1], point[2])));
            }
            (contour.holes ?? []).forEach((hole) => {
                if (!Array.isArray(hole) || hole.length < 3) {
                    return;
                }
                this.baseLoops.push(hole.map((point) => new THREE.Vector3(point[0], point[1], point[2])));
            });
        });
        this.rebuildLines();
    }

    /**
     * 按角度更新 ghost 显示。
     *
     * @param input 更新输入。
     */
    public updateByAngle(input: {
        axisOrigin: THREE.Vector3;
        axisDirection: THREE.Vector3;
        angle: number;
    }): void {
        if (this.ghostLines.length === 0) {
            return;
        }
        this.rotationQuaternion.setFromAxisAngle(
            normalizeRevolveAxisDirection(input.axisDirection),
            input.angle,
        );
        this.ghostLines.forEach((line, loopIndex) => {
            const baseLoop = this.baseLoops[loopIndex];
            const position = this.ghostPositionAttributes[loopIndex];
            if (!baseLoop || baseLoop.length < 3 || !position) {
                return;
            }
            this.updateClosedLinePosition(position, baseLoop, input.axisOrigin);
            position.needsUpdate = true;
            line.geometry.computeBoundingSphere();
        });
        this.visible = true;
    }

    /**
     * 释放 ghost 对象。
     */
    public dispose(): void {
        this.disposeLines();
        this.baseLoops = [];
    }

    /**
     * 重建线对象。
     */
    private rebuildLines(): void {
        this.disposeLines();
        this.clear();
        this.ghostPositionAttributes = [];
        this.ghostLines = this.baseLoops.map((loop) => {
            /**
             * WebGPU Renderer 不支持 THREE.LineLoop。
             * 这里用普通 Line 并重复首点闭合，保持同样视觉，同时兼容当前渲染器。
             */
            const positions = new Float32Array((loop.length + 1) * 3);
            this.writeClosedLineBasePositions(positions, loop);
            const position = new THREE.BufferAttribute(positions, 3);
            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', position);
            geometry.computeBoundingSphere();
            const line = new THREE.Line(
                geometry,
                new THREE.LineBasicMaterial({
                    color: GHOST_LINE_COLOR,
                    transparent: true,
                    opacity: 0.88,
                    depthTest: false,
                    depthWrite: false,
                }),
            );
            line.renderOrder = GHOST_RENDER_ORDER;
            line.frustumCulled = false;
            this.add(line);
            this.ghostPositionAttributes.push(position);
            return line;
        });
    }

    /**
     * 写入闭合线基础坐标。
     *
     * @param positions 坐标缓冲。
     * @param loop 轮廓点列。
     */
    private writeClosedLineBasePositions(positions: Float32Array, loop: THREE.Vector3[]): void {
        loop.forEach((point, index) => {
            positions[index * 3] = point.x;
            positions[index * 3 + 1] = point.y;
            positions[index * 3 + 2] = point.z;
        });
        const firstPoint = loop[0];
        if (!firstPoint) {
            return;
        }
        const closingIndex = loop.length * 3;
        positions[closingIndex] = firstPoint.x;
        positions[closingIndex + 1] = firstPoint.y;
        positions[closingIndex + 2] = firstPoint.z;
    }

    /**
     * 更新闭合线旋转后坐标。
     *
     * @param position 坐标属性。
     * @param baseLoop 原始轮廓点列。
     * @param axisOrigin 旋转轴原点。
     */
    private updateClosedLinePosition(
        position: THREE.BufferAttribute,
        baseLoop: THREE.Vector3[],
        axisOrigin: THREE.Vector3,
    ): void {
        baseLoop.forEach((point, pointIndex) => {
            this.rotatedPoint
                .copy(point)
                .sub(axisOrigin)
                .applyQuaternion(this.rotationQuaternion)
                .add(axisOrigin);
            position.setXYZ(pointIndex, this.rotatedPoint.x, this.rotatedPoint.y, this.rotatedPoint.z);
            if (pointIndex === 0) {
                position.setXYZ(baseLoop.length, this.rotatedPoint.x, this.rotatedPoint.y, this.rotatedPoint.z);
            }
        });
    }

    /**
     * 释放所有线对象。
     */
    private disposeLines(): void {
        this.ghostLines.forEach((line) => {
            line.geometry.dispose();
            const material = line.material;
            if (Array.isArray(material)) {
                material.forEach((item) => item.dispose());
            } else if (material) {
                material.dispose();
            }
        });
        this.ghostLines = [];
        this.ghostPositionAttributes = [];
    }
}
